import { Users } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { BreadcrumbTrail } from "../molecules/BreadcrumbTrail";
import { CopyButton } from "../molecules/CopyButton";
import { ErrorState } from "../molecules/ErrorState";
import { MetricTile } from "../molecules/MetricTile";
import { Skeleton } from "../molecules/Skeleton";
import { TrustBadge, type TrustStatus } from "../molecules/TrustBadge";
import {
	EventTimeline,
	type EventTimelineItem,
} from "../organisms/EventTimeline";
import {
	RespaChecklist,
	type RespaChecklistProps,
} from "../organisms/RespaChecklist";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../primitives/Tooltip";

export interface LoanAuditSummary {
	eventCount: number;
	verifiedCount: number;
	anchoredCount: number;
	pendingCount: number;
	respaCompliant: boolean;
	respaCompletedCount: number;
	firstEventAt?: string | null;
	lastEventAt?: string | null;
}

export interface LoanAuditPageProps {
	loanIdHash: string;
	/** Human-readable loan reference shown as the page identity. */
	loanReference?: string;
	timeline: EventTimelineItem[];
	respa: RespaChecklistProps;
	summary: LoanAuditSummary;
	/**
	 * When true the overall summary failed to load while other data succeeded —
	 * the page renders the timeline and coverage with a gentle notice in place
	 * of the verdict and counts.
	 */
	summaryUnavailable?: boolean;
	/** Optional integrity signal that forces a tamper verdict. */
	tampered?: boolean;
	loading?: boolean;
	error?: Error | null;
	onGenerateReport: () => void;
	reportPending?: boolean;
	className?: string;
}

/**
 * Derives a single plain-language trust verdict for the whole loan from its
 * audit counts. A tamper signal wins; otherwise a loan is only "verified" when
 * every recorded action is both verified and permanently recorded.
 */
export function deriveLoanVerdict(
	summary: Pick<
		LoanAuditSummary,
		"eventCount" | "verifiedCount" | "anchoredCount"
	>,
	tampered = false,
): TrustStatus {
	if (tampered) {
		return "tamper";
	}
	if (
		summary.eventCount > 0 &&
		summary.verifiedCount === summary.eventCount &&
		summary.anchoredCount === summary.eventCount
	) {
		return "verified";
	}
	if (summary.verifiedCount > 0) {
		return "recorded";
	}
	return "processing";
}

function verdictSentence(status: TrustStatus, eventCount: number): string {
	const actions = `${eventCount} recorded ${
		eventCount === 1 ? "action" : "actions"
	}`;
	switch (status) {
		case "verified":
			return `All ${actions} on this loan are verified and permanently recorded.`;
		case "recorded":
			return `${actions} on this loan are confirmed; some are still being permanently recorded.`;
		case "tamper":
			return "One or more recorded actions could not be confirmed and need review.";
		default:
			return eventCount === 0
				? "No actions have been recorded for this loan yet."
				: "Recorded actions on this loan are being prepared and confirmed.";
	}
}

function LoanAuditPage({
	loanIdHash,
	loanReference,
	timeline,
	respa,
	summary,
	summaryUnavailable = false,
	tampered = false,
	loading = false,
	error = null,
	onGenerateReport,
	reportPending = false,
	className,
}: LoanAuditPageProps): ReactNode {
	const truncatedId = `${loanIdHash.slice(0, 8)}…`;
	const identity = loanReference ?? truncatedId;

	const breadcrumbs = [{ label: "Loans", href: "/loans" }, { label: identity }];

	const verdict = deriveLoanVerdict(summary, tampered);

	// Actions recorded by parties other than the user-of-record (e.g. a title
	// company). Normal for a loan, so this is shown neutrally with context.
	const otherPartyCount = timeline.filter(
		(e) => e.actorIsUserOfRecord === false,
	).length;

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<BreadcrumbTrail items={breadcrumbs} />

			{error ? (
				<ErrorState
					error={error}
					title="We couldn't find this loan"
					message="No verified records were found for this reference. Double-check the loan reference and try again."
				/>
			) : loading ? (
				<div className="flex flex-col gap-4">
					<Skeleton className="h-28 w-full" />
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
						{Array.from({ length: 4 }).map((_, i) => {
							// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
							return <Skeleton key={i} className="h-24" />;
						})}
					</div>
					<Skeleton className="h-64 w-full" />
				</div>
			) : (
				<>
					<div className="flex items-start justify-between gap-4 flex-wrap">
						<div className="flex flex-col gap-1">
							<Heading size="xl">{identity}</Heading>
							<div className="flex items-center gap-1.5">
								<Text size="xs" textColor="mutedForeground" as="span">
									Verification ID: {truncatedId}
								</Text>
								<span className="print:hidden">
									<CopyButton value={loanIdHash} />
								</span>
							</div>
						</div>
						<Button
							onClick={onGenerateReport}
							disabled={reportPending}
							className="print:hidden"
						>
							{reportPending
								? "Preparing certificate…"
								: "Download certificate (PDF)"}
						</Button>
					</div>

					{summaryUnavailable ? (
						<div className="rounded-lg border border-border bg-muted/40 p-4">
							<Text size="sm" textColor="mutedForeground">
								The overall summary is temporarily unavailable. The recorded
								timeline and settlement-service coverage below are still
								accurate.
							</Text>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							<div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-5">
								<TrustBadge status={verdict} />
								<Text size="lg" weight="medium">
									{verdictSentence(verdict, summary.eventCount)}
								</Text>
							</div>

							<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
								<MetricTile label="Actions" value={summary.eventCount} />
								<MetricTile label="Verified" value={summary.verifiedCount} />
								<MetricTile
									label="Permanently recorded"
									value={summary.anchoredCount}
								/>
								<MetricTile label="In progress" value={summary.pendingCount} />
							</div>
						</div>
					)}

					<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
						<div className="lg:col-span-1">
							<RespaChecklist {...respa} />
						</div>
						<div className="lg:col-span-2 flex flex-col gap-3">
							{otherPartyCount > 0 && (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild={true}>
											<span className="inline-flex w-fit cursor-help items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-mutedForeground">
												<Users
													className="h-3.5 w-3.5 shrink-0"
													aria-hidden="true"
												/>
												{otherPartyCount}{" "}
												{otherPartyCount === 1 ? "action" : "actions"} recorded
												by other parties (e.g. title company)
											</span>
										</TooltipTrigger>
										<TooltipContent side="top">
											<span className="block max-w-xs text-xs">
												This is normal. A loan involves several parties — title
												companies, appraisers and others — and their recorded
												actions appear here alongside yours.
											</span>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)}
							<EventTimeline
								events={timeline}
								emptyMessage="No actions to show yet."
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
LoanAuditPage.displayName = "LoanAuditPage";

export default LoanAuditPage;
export { LoanAuditPage };
